import React, { useContext, useMemo, useState } from 'react';
import { GET_ENTITY_PROPERTIES } from '@nile/graphql';
import { useQuery } from '@apollo/client';
import { cloneDeep, isEmpty, includes } from 'lodash';
import {
  getTranslatedText,
  isAuthorised,
  AppContext,
  BackgroundIcon,
  AdditionalInfoWrap,
  InlineEdit,
  InlineEditTableWrapper,
  InfoPopupCard,
  EventEmitter,
  getDisplayName
} from '@nile/components';
import { Tooltip } from 'react-clone-tippy';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import envConfig from '@nile/home/src/Config/env';
import {
  DependantCell,
  DualCell,
  TextCell
} from '@nile/mangifera/src/Components/Ticket';
import InlineSelect from '@nile/components/src/Components/InlineEdit/Components/InlineSelect';
import {
  getAccessor,
  getCellType,
  getDependantField,
  getParentField,
  isDependantField,
  isEditable
} from '@nile/mangifera/src/Pebbles/TicketContainer';
import InlineDependantSelect from '@nile/components/src/Components/InlineEdit/Components/InlineDependantSelect';
import { Avatar } from '@superops/ui-design-system';
import { Loading } from '@shopify/polaris';
import SiteSelect from '@nile/projects/src/Components/Inputs/SiteSelect';
import { SVGIcon } from '@superops/super-icons';

const AdditionalInformationWrapper = styled.div`
  height: 40px;
  .inline-edit--editview__wrapper {
    width: 200px;
  }
`;

const TooltipWrap = styled.div`
  > div {
    height: 100%;
  }
`;

const Field = styled.div`
  .editable-cell,
  .cell {
    font-weight: bold;
  }
  &:hover {
    .edit-actions {
      visibility: visible;
    }
  }
`;

const PROPERTY_ORDER = {
  CLIENT: ['client', 'site', 'requester']
};
const PROPERTY_ICON = {
  client: 'ClientLogo',
  site: 'Site',
  requester: ''
};

function renderIcon(fieldName, displayName) {
  if (PROPERTY_ICON[fieldName]) {
    return <SVGIcon name={PROPERTY_ICON[fieldName]} size="sm" />;
  }
  if (isEmpty(displayName)) {
    return <Avatar customer name="Owner" size="extraSmall" />;
  }
  return (
    <Avatar
      initials={displayName.slice(0, 1).toUpperCase()}
      name={displayName}
      size="extraSmall"
    />
  );
}

function ClientField(props) {
  const SHOW_DETAILS = {
    requester: InfoPopupCard
  };
  const { intl, ticket, creationForm, readOnly } = props;
  const { data: entityFields, loading, error } = useQuery(
    GET_ENTITY_PROPERTIES
  );
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user || {};
  const userRole = currentUser?.role?.roleFeatureList || [];
  let isEditAuthorised = isAuthorised(userRole, 'tickets.edit');
  if (typeof readOnly !== 'undefined') {
    isEditAuthorised = !readOnly;
  }
  const [isEditing, setIsEditing] = useState(false);

  // eslint-disable-next-line no-underscore-dangle
  const _entityFields = useMemo(() => {
    return (entityFields?.getEntitiesMeta ?? []).map((metaEntity) => {
      return {
        ...metaEntity,
        attributes: metaEntity.attributes.map((field) => {
          return {
            ...field,
            label: getTranslatedText(intl, field.label || field.name)
          };
        })
      };
    });
  }, [entityFields]);

  const allEntityFields = useMemo(() => {
    return _entityFields.find((el) => el.name === 'Ticket')?.attributes || [];
  }, [_entityFields]);

  const allClientFields = useMemo(() => {
    const fields = allEntityFields.filter(
      (el) => PROPERTY_ORDER.CLIENT.includes(el.name) && !el.isCustom
    );
    fields.sort((a, b) => (a.name > b.name ? 1 : -1));
    return fields;
  }, [allEntityFields]);

  if (loading) return <Loading />;
  if (error) return <div>Error...</div>;

  const CELL_TYPES = {
    Text: TextCell,
    Dependant: DependantCell,
    Dual: DualCell
  };
  const getCellProps = (col) => {
    return {
      type: getCellType(col),
      accessor: getAccessor(col),
      editable: creationForm ? true : isEditAuthorised && isEditable(col),
      actions: getActions(col),
      field: col,
      parentField: isDependantField(col.name)
        ? allEntityFields.find((el) => el.name === getParentField(col.name))
        : col,
      dependantField: isDependantField(col.name)
        ? col
        : allEntityFields.find((el) => el.name === getDependantField(col.name))
    };
  };
  const getDefaultValue = (fieldValue, fieldName) => {
    switch (fieldName) {
      case 'additionalRequester':
        return fieldValue?.length
          ? fieldValue.map((el) => {
              return {
                ...el,
                name: el.name,
                label: el.name,
                value: el.name
              };
            })
          : [];
      case 'site':
        return fieldValue
          ? {
              id: fieldValue.id,
              name: fieldValue.name,
              label: fieldValue.name,
              value: fieldValue.name
            }
          : '';
      default:
        return fieldValue
          ? { name: fieldValue, label: fieldValue, value: fieldValue }
          : '';
    }
  };

  const handleCallback = (callback, args) => {
    if (typeof callback === 'function') {
      callback(args);
    }
    EventEmitter.dispatch('refreshRecipients');
  };
  const updateCell = (updatedObj, arg2, arg3, callback) => {
    if (props.updateProperties) {
      props.updateProperties(updatedObj, handleCallback.bind(this, callback));
    }
  };

  const getActions = (col) => {
    switch (col.name) {
      default:
        return {
          save: updateCell
        };
    }
  };

  const updateSelection = (field, val) => {
    let serialisedValue = null;
    let updatedProperties = {};
    switch (field.name) {
      case 'additionalRequester':
        props.updateProperties(updatedProperties, props.refetchTicket);
        return;
      case 'site':
        serialisedValue = { id: val.id, name: val.name };
        break;
      default:
        serialisedValue = val.name;
    }
    props.updateSelection(field.name, serialisedValue);
  };

  function renderInline({ allProps, defaultValue, field, Tag }) {
    if (includes(['Text', 'Dependant'], allProps.type)) {
      if (field.name === 'site') {
        return (
          <InlineEditTableWrapper>
            <InlineEdit
              editable={allProps.editable}
              defaultValue={defaultValue}
              autoSave
              showProgress
              editView={(fieldProps) => {
                return (
                  <InlineSelect
                    defaultValue={defaultValue}
                    updateSelection={updateSelection}
                    _ticket={ticket}
                    field={field}
                    mode="edit"
                    updateFieldState={fieldProps.updateFieldState}
                    params={allProps}
                  />
                );
              }}
              readView={() => {
                return (
                  <InlineSelect
                    defaultValue={defaultValue}
                    updateSelection={updateSelection}
                    _ticket={ticket}
                    field={field}
                    mode="view"
                    params={allProps}
                  />
                );
              }}
              onConfirm={(value, callback) => {
                updateCell(value, null, null, callback);
              }}
            />
          </InlineEditTableWrapper>
        );
      }
      return (
        <Tag
          defaultValue={defaultValue}
          updateSelection={updateSelection}
          _ticket={ticket}
          field={field}
          params={allProps}
          client={ticket?.client}
        />
      );
    }

    return (
      <InlineEditTableWrapper>
        <InlineEdit
          editable={allProps.editable}
          defaultValue={defaultValue}
          autoSave
          showProgress
          editView={(fieldProps) => {
            return (
              <InlineDependantSelect
                defaultValue={defaultValue}
                updateSelection={updateSelection}
                _ticket={ticket}
                field={field}
                mode="edit"
                updateFieldState={fieldProps.updateFieldState}
                params={allProps}
              />
            );
          }}
          readView={() => {
            return (
              <InlineDependantSelect
                defaultValue={defaultValue}
                updateSelection={updateSelection}
                _ticket={ticket}
                field={field}
                mode="view"
                params={allProps}
              />
            );
          }}
          onConfirm={(value, callback) => {
            updateCell(value, null, null, callback);
          }}
          onEditStateChange={setIsEditing}
        />
      </InlineEditTableWrapper>
    );
  }
  return (
    <div>
      {allClientFields.length > 0 && (
        <AdditionalInformationWrapper className="flex center">
          <AdditionalInfoWrap className="full-height">
            {allClientFields.map((field) => {
              const fieldValue = ticket[field.name];
              const defaultValue = getDefaultValue(fieldValue, field?.name);
              const allProps = getCellProps(field);
              const detailsData = { ...ticket[field.name] };
              const Tag = CELL_TYPES[allProps.type];
              const displayName = getDisplayName(detailsData);

              const component = (
                <Field className="full-height">
                  <TooltipWrap className="flex col full-height jcenter">
                    <Tooltip
                      title={field.label}
                      position="top-start"
                      inertia
                      delay={300}
                      trigger="mouseenter"
                      animation="shift"
                      animateFill={false}
                    >
                      <div className="flex center full-height">
                        {renderIcon(field.name, displayName)}
                        {renderInline({ allProps, defaultValue, Tag, field })}
                      </div>
                    </Tooltip>
                  </TooltipWrap>
                </Field>
              );

              const InfoPopupComponent = SHOW_DETAILS[field.name];

              return InfoPopupComponent ? (
                <InfoPopupComponent
                  activator={component}
                  editMode={isEditing}
                  data={detailsData}
                  openRequesterModal={props?.openRequesterModal}
                />
              ) : (
                component
              );
            })}
          </AdditionalInfoWrap>
        </AdditionalInformationWrapper>
      )}
    </div>
  );
}

export default injectIntl(ClientField);
